import React, { createContext, useState } from 'react';
import { css, keyframes } from '@emotion/css';
import CreatedOrderContent from './CreatedOrderContent';
import PendingOrderContent from './PendingOrderContent';
import FutureOrderContent from './FutureOrderContent';
import { get, cond, has } from 'lodash';
import PropTypes from 'prop-types';
import appLabels from '../../utils/appLabels';

const boxStyle = css`
  border: 0.0625rem solid #ddd9d5;
  border-radius: 0.5625rem 0.5625rem 0.625rem 0.625rem;
  //width: fit-content; TODO - this issue popped again after prettify changes - needs to investigate
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 641px) {
    min-width: initial;
    width: 100%;
    max-width: initial;
  }
`;

const loadingAnim = keyframes`
    0% {background-position: 0 50%;}
    50% {background-position: 100% 50%;}
    100% {background-position: 0 50%;}
`;

const loadingStyle = css`
  & span,
  & svg,
  & .asset {
    color: rgba(0, 0, 0, 0);
    background: linear-gradient(270deg, #eeeeee, #cccccc);
    background-size: 400% 400%;
    animation: ${loadingAnim} 3s ease infinite;
  }

  & svg path {
    opacity: 0;
  }
`;

const fillWidthStyle = css`
  width: 100%;
`;

export type OrderContextType = {
  orderID: string;
  snsNextOrderId: string;
  orderDate: string;
  cartId?: string;
};
export const OrderContext = createContext<OrderContextType | null>(null);

const OrderTile = (props) => {
  const upcomingOrders = get(props, 'upcomingOrders', {});
  const [fillWidth, setFillWidth] = useState(get(props, 'fillWidth', false));
  const status = get(upcomingOrders, 'upcomingOrder.orderStatus');
  const preferences = get(props, 'preferencesData', {});
  const onExpand = get(props, 'onExpand');
  const expanded = get(props, 'expanded');
  const loading = get(props, 'loading', false);
  const content = cond([
    [
      () => /CREATED/i.test(status),
      () => {
        return (
          <CreatedOrderContent
            upcomingOrders={upcomingOrders}
            orderStatusMessage={appLabels.CreateOrderContent.orderStatus}
            onExpand={onExpand}
          />
        );
      },
    ],
    [
      () => /PREPARING/i.test(status),
      () => {
        return (
          <PendingOrderContent
            upcomingOrders={upcomingOrders}
            callbacks={{ setFillWidth }}
            onExpand={onExpand}
            hideOrderStatusMessage={true}
            preferences={preferences}
          />
        );
      },
    ],
    [
      () => /ECOM_CANCELLED/i.test(status),
      () => {
        return (
          <CreatedOrderContent
            upcomingOrders={upcomingOrders}
            orderStatusMessage={appLabels.OrderTileContent.cancelledOrderStatus}
            onExpand={onExpand}
          />
        );
      },
    ],
    [
      () => /WAITING_RETRY/i.test(status),
      () => {
        return (
          <PendingOrderContent
            upcomingOrders={upcomingOrders}
            callbacks={{ setFillWidth }}
            orderStatusMessage={appLabels.OrderTileContent.pendingOrderStatus}
            hideManageLink={true}
            onExpand={onExpand}
            showRescheduleLink={false}
          />
        );
      },
    ],
    [
      () => /FAILED/i.test(status),
      () => {
        return (
          <PendingOrderContent
            upcomingOrders={upcomingOrders}
            callbacks={{ setFillWidth }}
            orderStatusMessage={appLabels.OrderTileContent.cancelledOrderStatus}
            hideManageLink={true}
            onExpand={onExpand}
            showRescheduleLink={false}
          />
        );
      },
    ],
    [
      () => true,
      () => {
        return (
          <FutureOrderContent futureOrder={upcomingOrders} expanded={expanded} onExpand={onExpand} loading={loading} />
        );
      },
    ],
  ]);

  const _fillWidth = has(props, 'fillWidth') ? get(props, 'fillWidth', false) : fillWidth;

  return (
    <OrderContext.Provider
      value={{
        orderID: get(upcomingOrders, 'upcomingOrder.orderID'),
        snsNextOrderId: get(upcomingOrders, 'upcomingOrder.snsNextOrderId'),
        orderDate: get(upcomingOrders, 'upcomingOrder.orderDate'),
        cartId: get(upcomingOrders, 'cartId'),
      }}
    >
      <div className={`${boxStyle} ${loading ? loadingStyle : ''} ${_fillWidth ? fillWidthStyle : ''}`}>
        {content()}
      </div>
    </OrderContext.Provider>
  );
};

OrderTile.propTypes = {
  loading: PropTypes.bool,
  upcomingOrders: PropTypes.object,
  preferencesData: PropTypes.object,
  fillWidth: PropTypes.bool,
  expanded: PropTypes.bool,
  onExpand: PropTypes.func,
};

export default OrderTile;
